/**
 * DemoChat — webchat de demonstração reutilizável.
 *
 * Encapsula a lógica de conversa com um agente Libben via webchat público
 * (POST /webchat/{tenant}/message + polling em /webchat/{tenant}/poll).
 *
 * Usado no Marketplace, na Home e na landing de Saúde. O tenant e o agentId
 * são configuráveis por props para permitir apontar para agentes diferentes
 * (ex.: agente de agendamento da clínica na página /saude).
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import BrandIcon from './BrandIcon';
import { handleTalkToSpecialist } from '../utils/webchat';
import './DemoChat.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.libbenagents.com';

export default function DemoChat({
  tenant = 'demo',
  agentId,
  name = 'Assistente',
  icon = '🤖',
  welcome,
  placeholder,
  limit = 10,
  accent,
  className = '',
}) {
  const { t } = useTranslation();
  const welcomeText = welcome || t('marketplace.demoWelcome', { name });

  const [msgs, setMsgs] = useState([{ role: 'agent', text: welcomeText }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [sessionId] = useState(() => `demo_${Math.random().toString(36).slice(2, 14)}`);
  const msgsRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [msgs]);

  // Limpa o polling pendente ao desmontar
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading || count >= limit) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', text }]);
    setLoading(true);
    setCount((c) => c + 1);

    try {
      await fetch(`${API_URL}/webchat/${tenant}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, message: text, ...(agentId ? { agentId } : {}) }),
      });

      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        try {
          const r = await fetch(`${API_URL}/webchat/${tenant}/poll?sessionId=${sessionId}`);
          const data = await r.json();
          if (data.response) {
            clearInterval(pollRef.current);
            setMsgs((m) => [...m, { role: 'agent', text: data.response }]);
            setLoading(false);
          }
        } catch {
          /* mantém o polling até o limite de tentativas */
        }
        if (attempts > 15) {
          clearInterval(pollRef.current);
          setLoading(false);
        }
      }, 1500);
    } catch {
      setMsgs((m) => [...m, { role: 'agent', text: t('marketplace.demoError') }]);
      setLoading(false);
    }
  }, [input, loading, count, limit, tenant, agentId, sessionId, t]);

  const reachedLimit = count >= limit;
  const style = accent ? { '--demo-accent': accent } : undefined;

  return (
    <div className={`demo-chat ${className}`} style={style}>
      <div className="demo-chat-header">
        <BrandIcon icon={icon} size={24} />
        <span className="demo-chat-name">{name}</span>
        <span className="demo-badge">{t('marketplace.demoLabel', { defaultValue: 'Demo' })}</span>
      </div>

      <div className="demo-chat-msgs" ref={msgsRef}>
        {msgs.map((m, i) => (
          <div key={i} className={`demo-msg demo-msg-${m.role}`}>{m.text}</div>
        ))}
        {loading && (
          <div className="demo-msg demo-msg-agent">
            <span className="demo-typing"><span /><span /><span /></span>
          </div>
        )}
        {reachedLimit && (
          <div className="demo-limit">
            {t('marketplace.demoLimit')}{' '}
            <button type="button" className="demo-limit-link" onClick={handleTalkToSpecialist}>
              {t('marketplace.demoLimitCta')}
            </button>
          </div>
        )}
      </div>

      <div className="demo-input-row">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder={placeholder || t('marketplace.demoPlaceholder')}
          disabled={loading || reachedLimit}
          aria-label={placeholder || t('marketplace.demoPlaceholder')}
        />
        <button
          onClick={send}
          disabled={loading || reachedLimit || !input.trim()}
          aria-label="Enviar mensagem"
        >
          →
        </button>
      </div>
    </div>
  );
}
